import React from 'react';
import { BannerReact } from 'common/components/banner';
import { ActionEvent, BannerAction, BannerTheme, BannerType } from 'common/components/banner/types';
import { useTranslation } from 'common/i18n/hooks';
import { ButtonSize, ButtonTheme } from 'common/components/button/constants';
import { ButtonReact } from 'common/components/button';
import { SvgIconReact } from 'common/components/svg-icon';
import { SelectReact } from 'common/components/select';
import { ExperimentEndConditionType, ExperimentMetricType } from 'apps/experiments/src/types';
import { InputReact } from 'common/components/input';
import { InputType } from 'common/components/input/types';
import { SelectTheme } from 'common/components/select/constants';
import { EMPTY_METRIC_ID } from 'apps/experiments/src/store';
import { useExperimentPageContext } from 'iron/apps/experiments/src/components/context';
import { useExperimentMetrics } from 'iron/apps/experiments/src/components/hooks/metrics';
import { getEndConditions, getTestingModels } from 'iron/apps/experiments/src/components/page/configuration';
import { MAX_CONFIDENCE_LEVEL, MAX_END_AFTER_DAYS_SIZE, MIN_CONFIDENCE_LEVEL, MIN_END_AFTER_DAYS_SIZE, MIN_SAMPLE_SIZE } from 'iron/apps/experiments/src/components/utils';
import { useStagedExperiment } from 'iron/apps/experiments/src/components/hooks/staged-experiment';
import { TrackingEventSource } from 'iron/apps/experiments/src/components/tracking';
import { useLaunchExperiment } from 'iron/apps/experiments/src/components/hooks/launch-experiment';
import styles from './_configure.module.styl';
export function ExperimentPageBannerConfigure() {
  const {
    t
  } = useTranslation();
  const {
    canEdit
  } = useExperimentPageContext();
  const launchExperiment = useLaunchExperiment();
  const {
    commitExperiment
  } = useStagedExperiment();
  const [setupOpen, setSetupOpen] = React.useState(false);
  const onModalClose = async (save: boolean) => {
    setSetupOpen(false);
    if (save) {
      try {
        await commitExperiment();
        launchExperiment();
      } catch (_) {
        // do nothing, other actions throw errors
      }
    }
  };
  return <>
      <BannerReact headerText={t("experiments.banner.configure.title")} bodyText={t("experiments.banner.configure.body")} theme={BannerTheme.Base} type={BannerType.Embedded} icon='idea-blended' ctas={canEdit ? [{
      actionName: BannerAction.Dismiss,
      text: t("experiments.banner.configure.dismiss"),
      theme: ButtonTheme.Tertiary
    }, {
      actionName: BannerAction.Custom,
      text: t("experiments.banner.configure.cta"),
      theme: ButtonTheme.Primary
    }] : []} onAction={(ev: ActionEvent) => {
      if (ev.detail.actionName === BannerAction.Dismiss) {
        launchExperiment();
      } else if (ev.detail.actionName === BannerAction.Custom) {
        setSetupOpen(true);
      }
    }} data-sentry-element="BannerReact" data-sentry-source-file="_configure.tsx" />
      <ExperimentSetupModal setupOpen={setupOpen} onClose={onModalClose} data-sentry-element="ExperimentSetupModal" data-sentry-source-file="_configure.tsx" />
    </>;
}
function ExperimentSetupModal(_ref: {
  setupOpen: boolean;
  onClose: (save: boolean) => void;
}) {
  let {
    setupOpen,
    onClose
  }: {
    setupOpen: boolean;
    onClose: (save: boolean) => void;
  } = _ref;
  const {
    t
  } = useTranslation();
  const {
    metrics
  } = useExperimentMetrics();
  const firstPrimaryMetric = metrics.find(metric => metric.type === ExperimentMetricType.PRIMARY);
  const hasPrimaryMetric = firstPrimaryMetric && firstPrimaryMetric.id !== EMPTY_METRIC_ID;
  if (!setupOpen) {
    return null;
  }
  return <div className={styles.experimentSetupModal} data-sentry-component="ExperimentSetupModal" data-sentry-source-file="_configure.tsx">
      <div className={styles.experimentSetupHeader}>
        <div className={styles.experimentSetupTop}>
          <SvgIconReact icon='experiment' className={styles.icon} data-sentry-element="SvgIconReact" data-sentry-source-file="_configure.tsx" />
          <div className={styles.label}>{t("mixology.experiment")}</div>
        </div>
        <div className={styles.experimentSetupTitle}>{t("experiments.setup.title")}</div>
      </div>
      <div className={styles.experimentSetupBody}>
        <ExperimentSetupModalBody data-sentry-element="ExperimentSetupModalBody" data-sentry-source-file="_configure.tsx" />
      </div>
      <div className={styles.experimentSetupActions}>
        <ButtonReact onClick={() => onClose(false)} theme={ButtonTheme.Tertiary} icon='arrow-left' data-sentry-element="ButtonReact" data-sentry-source-file="_configure.tsx">
          {t("common.close")}
        </ButtonReact>
        <ButtonReact disabled={!hasPrimaryMetric} onClick={() => onClose(true)} theme={ButtonTheme.Primary} data-sentry-element="ButtonReact" data-sentry-source-file="_configure.tsx">
          {t("experiments.setup.finish")}
        </ButtonReact>
      </div>
    </div>;
}
function ExperimentSetupInput(_ref2: {
  label: string;
  helpText?: string;
  learnMoreUrl?: string;
  children: React.ReactNode;
}) {
  let {
    label,
    helpText,
    learnMoreUrl,
    children
  }: {
    label: string;
    helpText?: string;
    learnMoreUrl?: string;
    children: React.ReactNode;
  } = _ref2;
  const i18n = useTranslation();
  return <div className={styles.experimentSetupInput} data-sentry-component="ExperimentSetupInput" data-sentry-source-file="_configure.tsx">
      <div className={styles.inputHeader}>
        <div className={styles.label}>{label}</div>
        {helpText && <div className={styles.helpText}>
            {helpText} {learnMoreUrl && <a href={learnMoreUrl}>{i18n.t("common.learn-more")}</a>}
          </div>}
      </div>
      <div className={styles.inputWrapper}>{children}</div>
    </div>;
}
function ExperimentSetupModalBody() {
  var _stagedExperiment$set, _stagedExperiment$set2, _stagedExperiment$set3, _stagedExperiment$set4, _endConditions$find, _testingModels$find, _stagedExperiment$set7;
  const i18n = useTranslation();
  const {
    stagedExperiment,
    stageChanges
  } = useStagedExperiment();
  const {
    createMetric,
    metrics
  } = useExperimentMetrics();
  const firstPrimaryMetric = metrics.find(metric => metric.type === ExperimentMetricType.PRIMARY);
  const [advancedSettingsOpen, setAdvancedSettingsOpen] = React.useState(false);
  const endConditions = getEndConditions(i18n);
  const testingModels = getTestingModels(i18n);
  const hasPrimaryMetric = firstPrimaryMetric && firstPrimaryMetric.id !== EMPTY_METRIC_ID;
  return <>
      <ExperimentSetupInput label={i18n.t("experiments.setup.primary-metric")} helpText={i18n.t("experiments.setup.primary-metric-info")} data-sentry-element="ExperimentSetupInput" data-sentry-source-file="_configure.tsx">
        <ButtonReact className={styles.fullWidth} icon-right='caret-down' theme={ButtonTheme.Select} size={ButtonSize.Large} onClick={ev => {
        ev.stopPropagation();
        createMetric(ExperimentMetricType.PRIMARY, undefined, undefined, TrackingEventSource.ExperimentPageSetupModal);
      }} data-sentry-element="ButtonReact" data-sentry-source-file="_configure.tsx">
          <span>{hasPrimaryMetric ? firstPrimaryMetric.name : i18n.t("experiments.setup.primary-metric-btn")}</span>
        </ButtonReact>
      </ExperimentSetupInput>
      <ExperimentSetupInput label={i18n.t("experiments.setup.test-duration")} helpText={i18n.t("experiments.setup.test-duration-info")} data-sentry-element="ExperimentSetupInput" data-sentry-source-file="_configure.tsx">
        <div className={styles.endConditionWrapper}>
          {(stagedExperiment === null || stagedExperiment === void 0 || (_stagedExperiment$set = stagedExperiment.settings) === null || _stagedExperiment$set === void 0 ? void 0 : _stagedExperiment$set.endCondition) === ExperimentEndConditionType.SAMPLE_SIZE && <InputReact className={styles.endConditionValue} fieldId='experiment-end-condition-sample-size' placeholder={i18n.t("experiments.configuration.end-condition-options.sample-size-placeholder")} value={String(stagedExperiment === null || stagedExperiment === void 0 || (_stagedExperiment$set2 = stagedExperiment.settings) === null || _stagedExperiment$set2 === void 0 ? void 0 : _stagedExperiment$set2.sampleSize)} type={InputType.Number} min={String(MIN_SAMPLE_SIZE)} step='1' onInput={ev => {
          stageChanges({
            settings: {
              sampleSize: Number(ev.target.value)
            }
          });
        }} onChange={(ev: TODO) => {
          stageChanges({
            settings: {
              sampleSize: Number(ev.target.value)
            }
          });
        }} />}
          {(stagedExperiment === null || stagedExperiment === void 0 || (_stagedExperiment$set3 = stagedExperiment.settings) === null || _stagedExperiment$set3 === void 0 ? void 0 : _stagedExperiment$set3.endCondition) === ExperimentEndConditionType.DAYS && <InputReact className={styles.endConditionValue} fieldId='experiment-end-condition-days' placeholder={i18n.t("experiments.configuration.end-condition-options.days-placeholder")} value={String(stagedExperiment === null || stagedExperiment === void 0 || (_stagedExperiment$set4 = stagedExperiment.settings) === null || _stagedExperiment$set4 === void 0 ? void 0 : _stagedExperiment$set4.endAfterDays)} type={InputType.Number} min={String(MIN_END_AFTER_DAYS_SIZE)} max={String(MAX_END_AFTER_DAYS_SIZE)} step='1' onInput={ev => {
          stageChanges({
            settings: {
              endAfterDays: Number(ev.target.value)
            }
          });
        }} onChange={(ev: TODO) => {
          stageChanges({
            settings: {
              endAfterDays: Number(ev.target.value)
            }
          });
        }} />}
          <SelectReact className={styles.endConditionType} selectedOption={(_endConditions$find = endConditions.find(condition => {
          var _stagedExperiment$set5;
          return condition.value === (stagedExperiment === null || stagedExperiment === void 0 || (_stagedExperiment$set5 = stagedExperiment.settings) === null || _stagedExperiment$set5 === void 0 ? void 0 : _stagedExperiment$set5.endCondition);
        })) === null || _endConditions$find === void 0 ? void 0 : _endConditions$find.label} disableSearch alwaysShowCaret theme={SelectTheme.Select} size={ButtonSize.Large} menuWidth={300} sections={[{
          items: endConditions.map(condition => ({
            key: condition.value,
            label: condition.label,
            wrapSubLabel: true,
            subLabel: condition.tooltipLabel,
            value: condition.value
          }))
        }]} onChange={(ev: TODO) => stageChanges({
          settings: {
            endCondition: ev.detail.item.value
          }
        })} data-sentry-element="SelectReact" data-sentry-source-file="_configure.tsx" />
        </div>
      </ExperimentSetupInput>
      <div className={styles.hr} />
      <div className={styles.advancedToggle}>
        <span>{i18n.t("experiments.setup.advanced-settings")}</span>
        <ButtonReact icon={advancedSettingsOpen ? "caret-down" : "caret-up"} theme={ButtonTheme.Tertiary} onClick={() => setAdvancedSettingsOpen(!advancedSettingsOpen)} data-sentry-element="ButtonReact" data-sentry-source-file="_configure.tsx" />
      </div>
      {advancedSettingsOpen && <>
          <ExperimentSetupInput label={i18n.t("experiments.setup.test-type")} helpText={i18n.t("experiments.setup.test-type-info")}>
            <SelectReact className={styles.fullWidth} selectedOption={(_testingModels$find = testingModels.find(condition => {
          var _stagedExperiment$set6;
          return condition.value === (stagedExperiment === null || stagedExperiment === void 0 || (_stagedExperiment$set6 = stagedExperiment.settings) === null || _stagedExperiment$set6 === void 0 ? void 0 : _stagedExperiment$set6.testingModel);
        })) === null || _testingModels$find === void 0 ? void 0 : _testingModels$find.label} disableSearch alwaysShowCaret theme={SelectTheme.Select} size={ButtonSize.Large} menuWidth={300} sections={[{
          items: testingModels.map(condition => ({
            key: condition.value,
            label: condition.label,
            wrapSubLabel: true,
            subLabel: condition.tooltipLabel,
            value: condition.value
          }))
        }]} onChange={(ev: TODO) => stageChanges({
          settings: {
            endCondition: ev.detail.item.value
          }
        })} />
          </ExperimentSetupInput>
          <ExperimentSetupInput label={i18n.t("experiments.setup.confidence")} helpText={i18n.t("experiments.setup.confidence-info")}>
            <InputReact className={styles.fullWidth} fieldId='experiment-confidence-level' value={String(stagedExperiment === null || stagedExperiment === void 0 || (_stagedExperiment$set7 = stagedExperiment.settings) === null || _stagedExperiment$set7 === void 0 ? void 0 : _stagedExperiment$set7.confidenceLevel)} type={InputType.Number} min={String(MIN_CONFIDENCE_LEVEL)} max={String(MAX_CONFIDENCE_LEVEL)} step='0.01' onInput={ev => {
          stageChanges({
            settings: {
              confidenceLevel: Number(ev.target.value)
            }
          });
        }} onChange={(ev: TODO) => {
          stageChanges({
            settings: {
              confidenceLevel: Number(ev.target.value)
            }
          });
        }} />
          </ExperimentSetupInput>
        </>}
    </>;
}